import React, { useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import Navbar from '@/components/Navbar';
import axios from 'axios';
import { Copy, Check, AlertTriangle, ExternalLink, RefreshCw, CheckCircle, PlayCircle, Landmark } from 'lucide-react';
import { UsdtTrxIcon } from '@/components/UsdtTrxIcon';
import { ENABLE_CRYPTO_PAYMENTS } from '@/config/featureFlags';

const C = {
  bg: '#131413',
  accent: '#9EAFAC',
  accentDark: '#586060',
  border: 'rgba(158,175,172,0.15)',
  card: 'rgba(19,20,19,0.9)',
};

export default function CheckoutPage() {
  const { user, getHeaders, API, SUPPORT_WHATSAPP_URL, SUPPORT_WHATSAPP_NUMBER } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(ENABLE_CRYPTO_PAYMENTS ? 'method' : 'bank-transfer');
  const [method, setMethod] = useState(ENABLE_CRYPTO_PAYMENTS ? 'crypto' : 'bank_transfer');
  const [paymentData, setPaymentData] = useState(null);
  const [bankConfig, setBankConfig] = useState(null);
  const [manualReport, setManualReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);
  const [statusMsg, setStatusMsg] = useState('');
  const [statusType, setStatusType] = useState('neutral');

  useEffect(() => {
    const loadBankConfig = async () => {
      try {
        const res = await axios.get(`${API}/payments/bank-transfer-config`, { headers: getHeaders() });
        setBankConfig(res.data);
      } catch {
        setBankConfig(null);
      }
    };
    loadBankConfig();
  }, [API, getHeaders]);

  useEffect(() => {
    if (ENABLE_CRYPTO_PAYMENTS) return;
    if (method === 'crypto') setMethod('bank_transfer');
    if (step === 'method' || step === 'info' || step === 'payment') setStep('bank-transfer');
  }, [method, step]);

  if (user?.investment_status === 'active') {
    return (
      <div style={{ minHeight: '100vh', background: C.bg, color: 'white', fontFamily: 'Inter, sans-serif' }}>
        <Navbar />
        <div style={{ paddingTop: '120px', display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 'calc(100vh - 80px)' }}>
          <div style={{ textAlign: 'center', animation: 'fade-up 0.6s ease both' }}>
            <div style={{ width: '80px', height: '80px', borderRadius: '50%', background: 'rgba(158,175,172,0.1)', border: `2px solid ${C.accent}`, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 24px' }}>
              <CheckCircle size={40} color={C.accent} />
            </div>
            <h2 style={{ fontFamily: 'Outfit, sans-serif', fontSize: '28px', fontWeight: 700, color: 'white', marginBottom: '12px' }}>¡Ya tienes una inversión activa!</h2>
            <p style={{ color: C.accentDark, marginBottom: '28px' }}>Tu Plan Multiplicador ya está activo y generando ganancias.</p>
            <button
              onClick={() => navigate('/dashboard')}
              className="btn-glow"
              style={{
                padding: '14px 32px', background: C.accent, border: 'none', borderRadius: '50px',
                color: '#131413', fontWeight: 800, fontSize: '16px', cursor: 'pointer',
                fontFamily: 'Outfit, sans-serif', boxShadow: `0 0 24px rgba(158,175,172,0.25)`,
              }}
            >
              Ver Mi Billetera
            </button>
          </div>
        </div>
      </div>
    );
  }

  const createPayment = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await axios.post(`${API}/payments/create`, {}, { headers: getHeaders() });
      setPaymentData(res.data);
      setStep('payment');
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al crear el pago. Intenta de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  const reportBankPayment = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await axios.post(`${API}/payments/report-bank-transfer`, {}, { headers: getHeaders() });
      setManualReport(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'No se pudo reportar el pago manual.');
    } finally {
      setLoading(false);
    }
  };

  const copyAddress = () => {
    navigator.clipboard.writeText(paymentData.pay_address);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const checkStatus = async () => {
    setStatusMsg('');
    try {
      const res = await axios.get(`${API}/payments/status/${paymentData.payment_id}`, { headers: getHeaders() });
      const status = res.data.status;
      if (status === 'finished' || status === 'confirmed') {
        setStatusMsg('¡Pago confirmado! Redirigiendo a tu billetera...');
        setStatusType('success');
        setTimeout(() => navigate('/dashboard'), 2000);
      } else if (status === 'waiting') {
        setStatusMsg('Esperando pago... Envía el USDT a la dirección mostrada.');
        setStatusType('info');
      } else if (status === 'confirming') {
        setStatusMsg('Pago recibido. Confirmando en la red TRC20 (1–3 minutos)...');
        setStatusType('info');
      } else {
        setStatusMsg(`Estado: ${status}`);
        setStatusType('neutral');
      }
    } catch {
      setStatusMsg('Error verificando estado. Intenta de nuevo.');
      setStatusType('neutral');
    }
  };

  const rowStyle = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 0', borderBottom: `1px solid ${C.border}` };
  const labelStyle = { color: C.accentDark, fontSize: '14px' };
  const stepBadge = { width: '32px', height: '32px', borderRadius: '50%', background: 'rgba(158,175,172,0.12)', border: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 };

  const bt = {
    bank_name: bankConfig?.bank_name || 'Produbanco',
    account_type: bankConfig?.account_type || 'Cuenta de ahorros',
    account_holder: bankConfig?.account_holder || 'Clara Paredes',
    id_number: bankConfig?.id_number || '1714550785',
    account_number: bankConfig?.account_number || '20004534458',
    whatsapp_number: bankConfig?.whatsapp_number || SUPPORT_WHATSAPP_NUMBER,
    whatsapp_url: bankConfig?.whatsapp_url || SUPPORT_WHATSAPP_URL,
  };

  return (
    <div style={{ minHeight: '100vh', background: C.bg, color: 'white', fontFamily: 'Inter, sans-serif' }}>
      <Navbar />

      <div style={{ paddingTop: '100px', paddingBottom: '64px', padding: '100px 24px 64px' }}>
        <div style={{ maxWidth: '600px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: '48px', animation: 'fade-up 0.6s ease both' }}>
            <p style={{ color: C.accentDark, fontSize: '12px', fontWeight: 700, letterSpacing: '0.2em', textTransform: 'uppercase', marginBottom: '12px' }}>Activar Plan</p>
            <h1 style={{ fontFamily: 'Outfit, sans-serif', fontSize: 'clamp(32px, 5vw, 48px)', fontWeight: 900, color: 'white', marginBottom: '8px', letterSpacing: '-1px' }}>Realizar Pago</h1>
            <p style={{ color: C.accentDark }}>Plan Multiplicador · $100 USD</p>
          </div>

          {step === 'method' && ENABLE_CRYPTO_PAYMENTS && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <button onClick={() => { setMethod('crypto'); setStep('info'); }} style={{ textAlign: 'left', background: C.card, border: `1px solid ${C.border}`, borderRadius: '16px', padding: '20px', color: 'white', cursor: 'pointer' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}><UsdtTrxIcon size="sm" /><strong>Pagar con Criptomonedas</strong></div>
                <p style={{ color: C.accentDark, margin: '8px 0 0' }}>Flujo automático con USDT TRC20 (NOWPayments).</p>
              </button>
              <button onClick={() => { setMethod('bank_transfer'); setStep('bank-transfer'); }} style={{ textAlign: 'left', background: C.card, border: `1px solid ${C.border}`, borderRadius: '16px', padding: '20px', color: 'white', cursor: 'pointer' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}><Landmark size={18} color={C.accent} /><strong>Pagar con Transferencia Bancaria</strong></div>
                <p style={{ color: C.accentDark, margin: '8px 0 0' }}>Reporte manual y verificación por admin.</p>
              </button>
            </div>
          )}

          {step === 'info' && method === 'crypto' && ENABLE_CRYPTO_PAYMENTS && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px', animation: 'fade-up 0.5s ease both' }}>
              <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: '20px', padding: '24px' }}>
                <h3 style={{ fontFamily: 'Outfit, sans-serif', fontSize: '16px', fontWeight: 700, color: 'white', marginBottom: '4px' }}>Resumen del Pago</h3>
                <div style={{ ...rowStyle }}><span style={labelStyle}>Plan</span><span style={{ color: 'white', fontWeight: 600 }}>Plan Multiplicador</span></div>
                <div style={{ ...rowStyle }}><span style={labelStyle}>Monto</span><span style={{ color: C.accent, fontFamily: 'Outfit, sans-serif', fontWeight: 900, fontSize: '22px' }}>$100.00 USDT</span></div>
                <div style={{ ...rowStyle }}><span style={labelStyle}>Red</span><div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}><UsdtTrxIcon size="sm" /><span style={{ color: 'white', fontWeight: 600 }}>TRC20 (TRON)</span></div></div>
              </div>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', background: 'rgba(217,119,6,0.08)', border: '1px solid rgba(217,119,6,0.25)', borderRadius: '16px', padding: '16px' }}><AlertTriangle size={20} color="#d97706" style={{ flexShrink: 0, marginTop: '1px' }} /><div><p style={{ color: '#f59e0b', fontWeight: 700, fontSize: '14px', marginBottom: '4px' }}>Verifica la Red Antes de Enviar</p><p style={{ color: 'rgba(245,158,11,0.75)', fontSize: '13px', lineHeight: 1.6 }}>Solo aceptamos pagos en la red <strong>TRON (TRC20)</strong>. Si envías por otra red (ERC20, BEP20, etc.) tus fondos podrían perderse sin recuperación.</p></div></div>
              <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: '20px', padding: '24px' }}>
                <h3 style={{ fontFamily: 'Outfit, sans-serif', fontSize: '16px', fontWeight: 700, color: 'white', marginBottom: '20px' }}>Cómo Comprar USDT en Binance</h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', marginBottom: '24px' }}>
                  {[{ n: '1', t: 'Crea tu cuenta Binance', d: 'Si no tienes cuenta, créala en Binance. Es gratis y rápido.' }, { n: '2', t: 'Compra USDT via P2P', d: 'Ve a Mercado P2P → Comprar → USDT → Selecciona un vendedor → Realiza el pago.' }, { n: '3', t: 'Envía a la dirección TRC20', d: 'Ve a Billetera → Spot → USDT → Retirar → Selecciona TRC20 → Pega la dirección.' }].map(item => (
                    <div key={item.n} style={{ display: 'flex', alignItems: 'flex-start', gap: '14px' }}><div style={{ ...stepBadge }}><span style={{ color: C.accent, fontWeight: 700, fontSize: '13px' }}>{item.n}</span></div><div><div style={{ color: 'white', fontWeight: 600, fontSize: '14px', marginBottom: '3px' }}>{item.t}</div><div style={{ color: C.accentDark, fontSize: '13px', lineHeight: 1.5 }}>{item.d}</div></div></div>
                  ))}
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px' }}>
                  {[{ href: 'https://www.youtube.com/shorts/il9lQ5f6YaI', title: 'Tutorial Binance', sub: 'Crear cuenta', testid: 'binance-tutorial-link' }, { href: 'https://youtu.be/wXuWTdeXbqo', title: 'Tutorial P2P', sub: 'Comprar USDT', testid: 'p2p-tutorial-link' }].map(link => (
                    <a key={link.testid} href={link.href} target="_blank" rel="noopener noreferrer" data-testid={link.testid} style={{ display: 'flex', alignItems: 'center', gap: '12px', background: 'rgba(158,175,172,0.05)', border: `1px solid ${C.border}`, borderRadius: '12px', padding: '14px', textDecoration: 'none', transition: 'all 0.2s', color: 'inherit' }}><PlayCircle size={22} color="#ef4444" style={{ flexShrink: 0 }} /><div style={{ flex: 1 }}><div style={{ color: 'white', fontSize: '14px', fontWeight: 600 }}>{link.title}</div><div style={{ color: C.accentDark, fontSize: '12px' }}>{link.sub}</div></div><ExternalLink size={14} color={C.accentDark} /></a>
                  ))}
                </div>
              </div>
              {error && (<div style={{ display: 'flex', alignItems: 'center', gap: '10px', background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: '12px', padding: '14px', color: '#fc8181', fontSize: '14px' }}><AlertTriangle size={16} />{error}</div>)}
              <button onClick={createPayment} disabled={loading} data-testid="generate-payment-button" className="btn-glow" style={{ width: '100%', padding: '18px', background: loading ? C.accentDark : C.accent, border: 'none', borderRadius: '16px', color: '#131413', fontFamily: 'Outfit, sans-serif', fontWeight: 800, fontSize: '18px', cursor: loading ? 'not-allowed' : 'pointer' }}>
                {loading ? 'Generando...' : 'Generar Dirección de Pago'}
              </button>
            </div>
          )}

          {step === 'bank-transfer' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: '20px', padding: '24px' }}>
                <h3 style={{ fontFamily: 'Outfit, sans-serif', marginTop: 0 }}>Transferencia Bancaria</h3>
                <div style={rowStyle}><span style={labelStyle}>Banco</span><strong>{bt.bank_name}</strong></div>
                <div style={rowStyle}><span style={labelStyle}>Tipo</span><strong>{bt.account_type}</strong></div>
                <div style={rowStyle}><span style={labelStyle}>Nombre</span><strong>{bt.account_holder}</strong></div>
                <div style={rowStyle}><span style={labelStyle}>Identificación</span><strong>{bt.id_number}</strong></div>
                <div style={{ ...rowStyle, borderBottom: 'none' }}><span style={labelStyle}>Número de cuenta</span><strong>{bt.account_number}</strong></div>
              </div>
              <button onClick={reportBankPayment} disabled={loading} className="btn-glow" style={{ width: '100%', padding: '16px', borderRadius: '12px', border: 'none', background: loading ? '#586060' : '#9EAFAC', color: '#131413', fontWeight: 800, cursor: loading ? 'not-allowed' : 'pointer', fontSize: '16px', fontFamily: 'Outfit, sans-serif', opacity: loading ? 0.6 : 1, transition: 'all 0.2s' }}>
                {loading ? 'Enviando...' : 'Pago realizado'}
              </button>
              {error && (<div style={{ color: '#f87171' }}>{error}</div>)}
              {manualReport && (
                <div style={{ background: 'rgba(158,175,172,0.08)', border: `1px solid ${C.border}`, borderRadius: '12px', padding: '14px' }}>
                  <p style={{ marginTop: 0, color: C.accent }}>{manualReport.message}</p>
                  <a href={manualReport.whatsapp_url || bt.whatsapp_url} target="_blank" rel="noopener noreferrer" style={{ color: 'white' }}>
                    Envía el comprobante desde el número con el que te registraste en VaultGrow por WhatsApp ({manualReport.whatsapp_number || bt.whatsapp_number})
                  </a>
                </div>
              )}
            </div>
          )}

          {step === 'payment' && paymentData && ENABLE_CRYPTO_PAYMENTS && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px', animation: 'fade-up 0.5s ease both' }}>
              <div style={{ position: 'relative', background: C.card, border: `1px solid ${C.border}`, borderRadius: '20px', padding: '28px', overflow: 'hidden' }}>
                <div style={{ position: 'relative', zIndex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '24px' }}>
                    <UsdtTrxIcon size="lg" /><div><div style={{ color: C.accentDark, fontSize: '12px' }}>Envía exactamente el monto indicado</div></div>
                  </div>
                  <div style={{ marginBottom: '20px' }}><div style={{ color: C.accentDark, fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.12em', marginBottom: '6px' }}>Monto a Enviar</div><div style={{ fontFamily: 'Outfit, sans-serif', fontSize: '40px', fontWeight: 900, color: C.accent }}>{paymentData.pay_amount ? `${parseFloat(paymentData.pay_amount).toFixed(2)} USDT` : '100 USDT'}</div></div>
                  <div style={{ marginBottom: '20px' }}>
                    <div style={{ color: C.accentDark, fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.12em', marginBottom: '8px' }}>Dirección de Pago (TRC20)</div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', background: 'rgba(158,175,172,0.05)', border: `1px solid ${C.border}`, borderRadius: '12px', padding: '14px' }}>
                      <code data-testid="payment-address" style={{ color: 'white', fontSize: '13px', flex: 1, wordBreak: 'break-all', fontFamily: 'monospace' }}>{paymentData.pay_address}</code>
                      <button onClick={copyAddress} data-testid="copy-address-button" style={{ padding: '8px 14px', background: 'rgba(158,175,172,0.08)', border: `1px solid ${C.border}`, borderRadius: '8px', color: C.accent, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}>{copied ? <Check size={14} /> : <Copy size={14} />}{copied ? 'Copiado' : 'Copiar'}</button>
                    </div>
                  </div>
                  {statusMsg && (<div style={{ padding: '12px 14px', borderRadius: '10px', marginBottom: '16px', fontSize: '14px', background: statusType === 'success' ? 'rgba(158,175,172,0.1)' : 'rgba(255,255,255,0.04)', border: `1px solid ${statusType === 'success' ? C.border : 'rgba(255,255,255,0.08)'}`, color: statusType === 'success' ? C.accent : '#d1d5db' }}>{statusMsg}</div>)}
                  <button onClick={checkStatus} data-testid="check-payment-status-button" style={{ width: '100%', padding: '14px', background: 'rgba(158,175,172,0.06)', border: `1px solid ${C.border}`, borderRadius: '12px', color: C.accent, fontWeight: 600, fontSize: '15px', cursor: 'pointer' }}><RefreshCw size={16} /> Verificar Estado del Pago</button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
